import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { useTariffContext } from 'contexts/TariffSectionContext';
import { parse } from 'date-fns';
import {
  DropdownOption,
  Form,
  formModelGet,
  Loader,
  LoaderType,
  TariffCard,
  Tooltip,
  useExtendedReducer
} from '@portal/ui-library';
import { useApplicationHelpers } from 'hooks/useApplicationHelpers';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import flatMap from 'lodash/flatMap';
import intersection from 'lodash/intersection';
import { partnerField } from 'models';
import { PersonRoleEnum, Tariff, TariffSectionEnum } from 'models/extension-generated';
import { ReactNode, SyntheticEvent, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Markdown } from 'components/Markdown/Markdown';
import { useSearchParams } from 'react-router-dom';
import { colors } from 'styles/colors';
import { getMinimumDateForBirth } from 'utils/dates';
import { getInsuranceStartDates } from 'utils/getInsuranceStartDates';
import { IconDentistry } from './Icons/IconDentistry';
import { IconHomeHealth } from './Icons/IconHomeHealth';

import styles from './index.module.scss';
import { Button } from '@mui/material';

const { YesNo, DatePicker, Dropdown } = Form.Components;

export type TariffSectionProps = {
  partnerKeys: {
    birthDateKey: string;
    applicationStartKey: string;
  };
};

export const TariffSection = ({ partnerKeys }: TariffSectionProps) => {
  const { isLoading, tariffCards, setTab, tab } = useTariffContext();
  const { t } = useTranslation('tariffSecion');
  const [searchParams] = useSearchParams();
  const disableTariffSelection = searchParams.get('tarifauswahl-fixiert')?.toLowerCase() === 'true';
  const {
    dispatch,
    partnerId,
    state,
    reducer: { updateValue }
  } = useHealthInsuranceContext();
  const { giveTariff, removeTariffs } = useExtendedReducer(dispatch);
  const { getSinglePartnerByRole } = useApplicationHelpers(state.model);
  const holder = getSinglePartnerByRole(PersonRoleEnum.POLICY_HOLDER);
  const anchorRef = useRef<HTMLHeadingElement>(null);
  // Make the ref globally accessible
  useEffect(() => {
    (window as any).tariffSectionAnchorRef = anchorRef;
  }, []);

  const availableStartDates: Array<DropdownOption> = useMemo(getInsuranceStartDates, []);
  const currentCards = tariffCards.cards
    ?.filter((card) => card.section === tab)
    .sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));

  const selectedTariffsKey = partnerField(
    partnerId,
    `applicationInformation.tariffInformation.selectedTariffs`
  );
  const selectedTariffs: Array<Tariff> = formModelGet(state.model, selectedTariffsKey) || [];
  const hasHospitalTariff = selectedTariffs.some(
    (tariff) => tariff.section === TariffSectionEnum.STATIONAER
  );
  const hasDentalTariff = selectedTariffs.some(
    (tariff) => tariff.section === TariffSectionEnum.ZAHN
  );
  const sectionTariffs = flatMap(
    (tariffCards.cards?.filter((card) => card.section === tab) || []).map(
      (card) => card.tariffs || []
    )
  ).map((tariff: string) => {
    return tariff;
  });

  const cardSelection = intersection(
    selectedTariffs.map(({ id }) => id),
    sectionTariffs
  )[0];

  const handleTabChange = (_event: SyntheticEvent, newTab: TariffSectionEnum) => {
    if (newTab === TariffSectionEnum.STATIONAER) {
      setTab(TariffSectionEnum.STATIONAER);
    } else if (newTab === TariffSectionEnum.ZAHN) {
      setTab(TariffSectionEnum.ZAHN);
    }
  };

  const handleTariffChange = (val: string | undefined) => {
    if (val) {
      // add tariff to the list, but keep tariff from the other section
      const remainingTariffsFromSection: Array<Partial<Tariff>> = sectionTariffs
        .filter((tariff: string) => tariff !== val)
        .map((tariff: string) => {
          return { id: tariff, section: tab };
        });

      removeTariffs({ partnerId, tariffs: remainingTariffsFromSection });
      giveTariff({ partnerId, tariff: { id: val, section: tab } });
    } else {
      const allTariffsFromSection = sectionTariffs.map((tariff: string) => {
        return { id: tariff };
      });

      removeTariffs({ partnerId, tariffs: allTariffsFromSection });
    }

    // reset dependant values
    const hasDownloadedContractInformationDocumentPath = partnerField(
      holder.id || '',
      'applicationInformation.hasDownloadedContractInformationDocument'
    );

    updateValue({
      key: hasDownloadedContractInformationDocumentPath,
      value: undefined
    });
  };

  const renderQuestionTitle = ({
    text,
    tooltipText,
    tooltipContent
  }: {
    text: string;
    tooltipText: string;
    tooltipContent: ReactNode;
  }): ReactNode => {
    return (
      <>
        <Typography aria-label={`${text}. ${tooltipText}`} variant="bodySMRegular">
          {text}
        </Typography>
        <Tooltip
          placement="bottom-start"
          tooltipContent={<div className={styles.tooltipWrap}>{tooltipContent}</div>}
          tooltipOffset={5}
        >
          <Button variant="text" size="small" className="text-highlight-80">
            <InfoIcon />
          </Button>
        </Tooltip>
      </>
    );
  };
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`
    };
  }
  const renderTabs = () => {
    return (
      <Tabs
        className="mb-l hidden s:block"
        value={tab}
        aria-label={
          'Wählen Sie zwischen einer "Krankenhauszusatzversicherung" oder einer "Zahnzusatzversicherung". Sie können auch Tarife aus beiden Bereichen miteinander kombinieren.'
        }
        variant="fullWidth"
        aria-describedby="tariffSectionTitle"
        onChange={handleTabChange}
        centered
        TabIndicatorProps={{ sx: { bgcolor: colors.highlight[80] } }}
      >
        <Tab
          value={TariffSectionEnum.STATIONAER}
          label={t('tariffSection:additionalInsurance')}
          icon={<IconHomeHealth />}
          iconPosition="start"
          {...a11yProps(0)}
        />
        <Tab
          value={TariffSectionEnum.ZAHN}
          label={t('tariffSection:dentalInsurance')}
          icon={<IconDentistry />}
          iconPosition="start"
          {...a11yProps(1)}
        />
      </Tabs>
    );
  };
  const renderMobileTabs = () => {
    return (
      <Tabs
        className="mb-l block s:hidden"
        value={tab}
        aria-label={
          'Wählen Sie zwischen einer "Krankenhauszusatzversicherung" oder einer "Zahnzusatzversicherung". Sie können auch Tarife aus beiden Bereichen miteinander kombinieren.'
        }
        variant="fullWidth"
        orientation="vertical"
        centered
        aria-describedby="tariffSectionTitle"
        onChange={handleTabChange}
        TabIndicatorProps={{ sx: { bgcolor: colors.highlight[80] } }}
      >
        <Tab
          value={TariffSectionEnum.STATIONAER}
          className="w-full"
          label={t('tariffSection:additionalInsurance')}
          icon={<IconHomeHealth />}
          iconPosition="start"
          {...a11yProps(0)}
        />
        <Tab
          className="w-full"
          value={TariffSectionEnum.ZAHN}
          label={t('tariffSection:dentalInsurance')}
          icon={<IconDentistry />}
          iconPosition="start"
          {...a11yProps(1)}
        />
      </Tabs>
    );
  };

  return (
    <div className="px-[24px] s:mx-[80px]">
      <div className="bg-white-100 py-[16px] s:py-[32px]">
        <Typography
          component={'h1'}
          variant="headlineXXXLGBold"
          className="text-navigation-80 text-center s:text-left"
          tabIndex={0}
        >
          {t('insuredPersonStep:title0')}
        </Typography>
        <Typography
          ref={anchorRef}
          id="tariffSectionMainTitle"
          component={'h2'}
          aria-description="Alle Felder, die im Namen ein * halten, sind Pflichtfelder"
          variant="headlineXXLGBold"
          className="text-navigation-80 text-center s:text-left m-[0px] focus:outline-none focus:ring-2 focus:ring-highlight-80"
          tabIndex={0}
        >
          {t('insuredPersonStep:title1')}
        </Typography>
      </div>
      <fieldset>
        <legend className="sr-only">{t('insuredPersonStep:title1')}</legend>
        <div
          className={'grid grid-cols-1 s:grid-cols-2 gap-[16px] s:gap-[20px] mb-[1em] s:mb-[2em]'}
        >
          <DatePicker
            className="col-span-2 s:col-span-1"
            componentKey={partnerKeys.birthDateKey}
            label={t('tariffSection:dayOfBirth')}
            ariaDescribedby="tariffSectionMainTitle"
            minDate={parse(getMinimumDateForBirth(), 'yyyy-MM-dd', new Date())}
          />

          <Dropdown
            className="col-span-2 s:col-span-1"
            id="applicationStart"
            componentKey={partnerKeys.applicationStartKey}
            options={availableStartDates}
            label={t('tariffSection:startOfInsurance')}
            placeholder={t('tariffSection:choose')}
          />
        </div>
      </fieldset>
      <div id="tariffSectionTitle" className="bg-white-100">
        <Typography
          component={'h2'}
          variant="headlineXXLGBold"
          className="text-navigation-80 text-center s:text-left m-[0px] py-m"
        >
          {t('insuredPersonStep:title2')}
        </Typography>
      </div>
      {renderMobileTabs()}
      {renderTabs()}

      <div className={styles.subHeader}>
        <Typography variant="headlineXLGBold">
          {tab === TariffSectionEnum.STATIONAER
            ? t('tariffSection:additionalInsuranceNote')
            : t('tariffSection:dentalInsuranceNote')}
        </Typography>
      </div>

      <div className={classNames('s:flex')}>
        {isLoading ? (
          <div className="flex items-center justify-center h-[100px]">
            <Loader type={LoaderType.Circular} />
          </div>
        ) : (
          <div className="grid gap-[8px] grid-cols-1 s:grid-cols-2 l:grid-cols-3">
            {currentCards?.map((card) => (
              <TariffCard
                banner={card.banner}
                bannerAltText={card.bannerAltText}
                imageAltText={card.imageAltText}
                tarriffNameAltText={`${card.tariffNameAltText} ab ${card.defaultPrice} Euro / Monat`}
                stablePremiumTextAltText={card.stablePremiumTextAltText}
                image={`/images/${card.image}.jpg`}
                tariffName={card.tariffName}
                defaultPrice={card.defaultPrice}
                listOfHighlights={card.listOfHighlights}
                infoChip={card.infoChip}
                otherHighLights={card.otherHighlights}
                landingPageUrl={card.landingPageUrl}
                stablePremiumVisibility={card.stablePremiumVisibility}
                selected={cardSelection}
                stablePremiumText={card.stablePremiumText}
                tariffs={card.tariffs || ['']}
                disabled={disableTariffSelection}
                onChange={(val) => handleTariffChange(val)}
                tariffS={card.tariffs?.find((t) => t.toLowerCase().includes('_s')) || ''}
                tariffL={card.tariffs?.find((t) => t.toLowerCase().includes('_l')) || ''}
                shouldShowBanner={currentCards.some((c) => c.banner)}
              />
            ))}
          </div>
        )}
      </div>
      <fieldset className="mt-m">
        {hasDentalTariff ||
          (hasHospitalTariff && (
            <>
              <legend id="questionsHeadline">
                <Typography component={'h3'} variant="bodyMDBold" className="text-navigation-80">
                  {t('tariffSection:questionsHeadline')}
                </Typography>
              </legend>
            </>
          ))}
        {hasHospitalTariff && (
          <>
            <YesNo
              name={t('tariffSection:hasNationalInsurance')}
              question={renderQuestionTitle({
                text: t('tariffSection:hasNationalInsurance'),
                tooltipText: t('tariffSection:hasNationalInsuranceTooltip'),
                tooltipContent: (
                  <Markdown markdown={t('tariffSection:hasNationalInsuranceTooltip')} />
                )
              })}
              ariaDescribedby="questionsHeadline"
              componentKey={partnerField(partnerId, 'applicationInformation.hasNationalInsurance')}
              className="mt-[12px]"
            />

            <YesNo
              name={t('tariffSection:hasSupplementaryHospitalInsurance')}
              ariaDescribedby="questionsHeadline"
              question={renderQuestionTitle({
                text: t('tariffSection:hasSupplementaryHospitalInsurance'),
                tooltipText: t('tariffSection:hasSupplementaryHospitalInsuranceTooltip'),
                tooltipContent: (
                  <Markdown
                    markdown={t('tariffSection:hasSupplementaryHospitalInsuranceTooltip')}
                  />
                )
              })}
              componentKey={partnerField(
                partnerId,
                'applicationInformation.hasSupplementaryHospitalInsurance'
              )}
              className="mt-[12px]"
            />
          </>
        )}
        {hasDentalTariff && (
          <>
            <YesNo
              name="hasStatutoryInsurance"
              ariaDescribedby="questionsHeadline"
              question={renderQuestionTitle({
                text: t('tariffSection:hasStatutoryInsurance'),
                tooltipText: t('tariffSection:hasStatutoryInsuranceTooltip'),
                tooltipContent: (
                  <Markdown markdown={t('tariffSection:hasStatutoryInsuranceTooltip')} />
                )
              })}
              componentKey={partnerField(partnerId, 'applicationInformation.hasStatutoryInsurance')}
              className="mt-[12px] mb-[24px]"
            />
          </>
        )}
      </fieldset>
    </div>
  );
};
